import { flash, RangeSlider, SubmitButton } from "@xola/ui-kit";
import { nullableNumber } from "common";
import { isEmpty, isNil, isNull, isNumber } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import {
    AvailabilityTimelinePreference,
    CheckoutPreference,
    CheckoutPriceDisplay,
    Experience,
    Permission,
    SplitPaymentPreference,
    UserPreferences,
} from "types";
import { mixed, number, object, string } from "yup";
import { IsGranted } from "../../../Common/IsGranted";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { ContentLayout } from "../../../Layout/ContentLayout";
import {
    CancellationProtectionPreferenceForm,
    CancellationProtectionPreferenceValue,
    getEnabledExperienceSelection,
    saveExperienceSelection,
    useCancellationProtectionExperiences,
} from "./CancellationProtectionPreferenceForm";
import { CheckoutPreferencesForm } from "./CheckoutPreferenceForm";
import { SplitPaymentPreferenceForm } from "./SplitPaymentPreferenceForm";
import { TimelinePreferencesForm } from "./TimelinePreferenceForm";
import { api } from "@/api/api";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";

interface CheckoutPreferenceFormProps {
    checkout: CheckoutPreference;
    availabilityTimeline: AvailabilityTimelinePreference;
    splitPayment: SplitPaymentPreference;
    cancellationProtection: CancellationProtectionPreferenceValue;
}

export const CheckoutPreferences = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const { mutateAsync: saveCheckoutPreference, isLoading: isSavingCheckout } = useMutation(
        async (payload: CheckoutPreference) => {
            return api.post(`/users/${seller.id}/preferences/checkout`, payload);
        },
    );

    const { mutateAsync: saveAvailabilityTimelinePreference, isLoading: isSavingTimelinePreference } = useMutation(
        async (payload: AvailabilityTimelinePreference) => {
            return api.put(`/users/${seller.id}/preferences/availabilityTimeline`, payload);
        },
    );

    const { mutateAsync: saveSplitPaymentPreference, isLoading: isSavingSplitPaymentPreference } = useMutation(
        async (payload: SplitPaymentPreference) => {
            return api.put(`/users/${seller.id}/preferences/splitPayment`, payload);
        },
    );

    const isCancellationProtectionEligible = seller.preferences.cancellationProtection?.eligible ?? false;
    const { experiences, isFetching: isFetchingExperiences } = useCancellationProtectionExperiences(
        isCancellationProtectionEligible,
    );

    const { mutateAsync: saveCancellationProtection, isLoading: isSavingCancellationProtection } = useMutation(
        async (value: CancellationProtectionPreferenceValue) => {
            await api.post(`/sellers/${seller.id}/preferences/cancellationProtection`, { enabled: value.enabled });
            await saveExperienceSelection(experiences, value.experiences);
        },
    );

    const showSplitPayment = isGranted(Permission.SPLIT_PAYMENT_VIEW);
    const isSaving =
        isSavingCheckout ||
        isSavingTimelinePreference ||
        isSavingCancellationProtection ||
        (showSplitPayment ? isSavingSplitPaymentPreference : false);

    const handleSubmit = async (values: CheckoutPreferenceFormProps) => {
        try {
            const promises = [
                saveCheckoutPreference(values.checkout),
                saveAvailabilityTimelinePreference(values.availabilityTimeline),
            ];
            if (showSplitPayment) {
                promises.push(saveSplitPaymentPreference(values.splitPayment));
            }

            await Promise.all(promises);
            // Kept out of the batch above: this writes one call per changed product, sequentially.
            if (isCancellationProtectionEligible) {
                await saveCancellationProtection(values.cancellationProtection);
                invalidate("experiences");
            }
            invalidate("sellers", seller.id);
            flash.success("Successfully updated checkout preferences");
        } catch (error) {
            console.log("Error in updating checkout preferences", error);
        }
    };

    const validationSchema = object().shape({
        ...(showSplitPayment && {
            splitPayment: object().shape({
                paymentDeadline: nullableNumber().min(0, "should be a positive integer"),
            }),
        }),
        availabilityTimeline: object().shape({
            showOpenMax: mixed().when("showOpen", {
                is: true,
                then: number()
                    .nullable()
                    .test("invalid", "should be a positive integer", (value) => {
                        return isNull(value) || (!isNil(value) && value >= 0);
                    }),
            }),
            unavailableMessage: string()
                .nullable()
                .when(["showUnavailable", "showUnavailableMessage"], {
                    is: (showUnavailable: boolean, showUnavailableMessage: boolean) => {
                        return showUnavailable && showUnavailableMessage;
                    },
                    then: string().test("invalid", "is required", (value?: string | null) => {
                        return isNull(value) || (!isNil(value) && value.length > 0);
                    }),
                }),
        }),
    });

    return (
        <ContentLayout isWide isDisabled={isSaving}>
            <XolaFormik
                enableReinitialize
                validateOnChange={false}
                validationSchema={validationSchema}
                initialValues={getInitialValues(seller.preferences, experiences)}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, handleSubmit, handleReset, errors }) => (
                    <>
                        <h2 className="mt-5">Checkout Preferences</h2>
                        <div className="divide-y divide-gray-light pb-5">
                            <ContentLayout.Section title="Checkout Preferences">
                                <CheckoutPreferencesForm
                                    value={values.checkout}
                                    onChange={(value) => setFieldValue("checkout", value)}
                                />
                            </ContentLayout.Section>
                            <IsGranted permissions={Permission.X2_CHECKOUT_VIEW}>
                                <ContentLayout.Section title="X2 Checkout" description="Restricted to Xola Admins">
                                    <div className="space-y-8">
                                        <X2CheckoutProbabilitySlider
                                            value={values.checkout.x2Probability ?? 1}
                                            onChange={(x2Probability: number) => {
                                                return setFieldValue("checkout", { ...values.checkout, x2Probability });
                                            }}
                                        />
                                    </div>
                                </ContentLayout.Section>
                            </IsGranted>
                            <ContentLayout.Section title="Timeline Display">
                                <TimelinePreferencesForm
                                    value={values.availabilityTimeline}
                                    errors={errors.availabilityTimeline}
                                    onChange={(value) => setFieldValue("availabilityTimeline", value)}
                                />
                            </ContentLayout.Section>
                            {isCancellationProtectionEligible && (
                                <ContentLayout.Section
                                    title="Cancellation Protection"
                                    description="Offer cancellation protection at checkout, and choose which products offer it"
                                >
                                    <CancellationProtectionPreferenceForm
                                        value={values.cancellationProtection}
                                        experiences={experiences}
                                        isFetching={isFetchingExperiences}
                                        onChange={(value) => setFieldValue("cancellationProtection", value)}
                                    />
                                </ContentLayout.Section>
                            )}
                            <IsGranted permissions={Permission.SPLIT_PAYMENT_VIEW}>
                                <ContentLayout.Section title="Split Payment">
                                    <SplitPaymentPreferenceForm
                                        value={values.splitPayment}
                                        errors={errors.splitPayment}
                                        onChange={(value: SplitPaymentPreference) =>
                                            setFieldValue("splitPayment", value)
                                        }
                                    />
                                </ContentLayout.Section>
                            </IsGranted>
                        </div>

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton isLoading={isSaving} onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </>
                )}
            </XolaFormik>
        </ContentLayout>
    );
};

const getInitialValues = (userPreferences: UserPreferences, experiences: Experience[]): CheckoutPreferenceFormProps => {
    const availabilityTimeline = userPreferences?.availabilityTimeline
        ? {
              ...userPreferences?.availabilityTimeline,
              showUnavailableMessage: !isEmpty(userPreferences?.availabilityTimeline.unavailableMessage),
          }
        : { maxDays: 7, showPrice: true, showOpen: true, showUnavailable: false, showUnavailableMessage: false };

    const initialValues = {
        checkout: {
            ...(userPreferences?.checkout ?? {
                displayEventDuration: false,
                priceDisplay: CheckoutPriceDisplay.LOWEST,
                includeFees: false,
                showTaxesAndFeesBreakdown: false,
                footerLinks: [],
                x2Probability: isNumber(userPreferences?.checkout?.x2Probability)
                    ? userPreferences?.checkout?.x2Probability
                    : 1,
                captchaEnabled: userPreferences?.checkout?.captchaEnabled ?? false,
            }),
        },
        splitPayment: userPreferences.splitPayment ?? { isEnabled: false, autoChargeOnDeadline: false },
        availabilityTimeline: availabilityTimeline,
        cancellationProtection: {
            enabled: userPreferences.cancellationProtection?.enabled ?? false,
            experiences: getEnabledExperienceSelection(experiences),
        },
    };

    /**
     * V2 Does not supports BASE PriceDisplay, we need to show LOWEST if thats the case
     */
    if (initialValues.checkout.priceDisplay === CheckoutPriceDisplay.BASE) {
        initialValues.checkout.priceDisplay = CheckoutPriceDisplay.LOWEST;
    }

    return initialValues;
};

const X2CheckoutProbabilitySlider = ({ value, onChange }: { value: number; onChange: (value: number) => void }) => {
    return (
        <div className="mt-4 flex flex-col gap-2">
            <div className="flex items-center space-x-1 pt-6">
                <span className="font-semibold">For Xola admins only:</span>
                <span>This setting determines the frequency at which the new checkout is presented to customers.</span>
            </div>
            <div className="flex w-150 items-center gap-4">
                <span>Always Open Old Checkout</span>
                <RangeSlider
                    min={0}
                    max={100}
                    step={1}
                    tooltipSuffix="%"
                    values={[value != null ? value * 100 : 100]}
                    onChange={(vals: string[]) => {
                        const sliderValue = Math.max(0, Math.min(100, Number.parseFloat(vals[0])));
                        onChange(sliderValue / 100);
                    }}
                />
                <span className="ml-2">Always Open New Checkout</span>
            </div>
        </div>
    );
};
